import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { useAuthStore } from '../stores/authStore.js'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import AccountLayout from '../layouts/AccountLayout.vue'
// import RouteViewComponent from '../layouts/RouterBypass.vue'
import App from '../App.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'reputation',
        path: 'reputation',
        component: () => import('../pages/admin/reputation/Reputation.vue'),
      },
      {
        name: 'logout',
        path: 'logout',
        component: () => import('../pages/auth/Logout.vue'),
      },
      {
        name: 'inbox-monitor',
        path: 'inbox-monitor',
        component: () => import('../pages/admin/inbox-placement/InboxPlacement.vue'),
      },
      {
        name: 'admin-panel',
        path: 'admin-panel',
        component: () => import('../pages/admin-panel/AdminPanel.vue'),
        meta: { requiresAuth: true, roles: ['admin', 'superadmin', 'external_manager'] },
      },
    ],
  },
  {
    name: 'account',
    path: '/account',
    component: AccountLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'profile',
        path: 'profile',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'alerts',
        path: 'alerts',
        component: () => import('../pages/preferences/Alerts.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'netcore-signup',
        path: 'netcore-signup',
        component: () => import('../pages/auth/NetcoreSignup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password/:base64encodedEmail/:tokenhash',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

const pinia = createPinia()
const app = createApp(App)

app.use(pinia)

const authStore = useAuthStore()

router.beforeEach((to, from, next) => {
  if (to.meta.requiresAuth && authStore.token === null) {
    console.log('Login redirect triggered')
    next({ name: 'login' })
    return
  }

  const allowedRoles = to.meta.roles as string[] | undefined
  if (allowedRoles && !allowedRoles.includes(authStore.role)) {
    next({ name: authStore.isExternal ? 'inbox-monitor' : 'dashboard' })
    return
  }

  // Defense in depth: external users are restricted to Inbox Monitor only - no
  // Dashboard/Reputation, and no Account/Settings/Alerts either - even if they
  // navigate directly to a URL that the nav/dropdown already hide from them.
  const externalBlockedRoutes = ['dashboard', 'reputation', 'settings', 'profile', 'alerts']
  if (authStore.isExternal && externalBlockedRoutes.includes(to.name as string)) {
    next({ name: 'inbox-monitor' })
    return
  }

  next()
})

export default router
